import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import notificacaoService from '../../services/notificacao.service';
import {
  TIPOS_NOTIFICACAO, iconeDaNotificacao, visualDaNotificacao, tipoNormalizado,
} from '../../constants/iconesNotificacoes';
import { statusDaTarefa, iconeDoStatus } from '../../constants/statusTarefa';

// ════════════════════════════════════════════════════════════════════════════
// <PopupNotificacoesTarefas> + useAlertaNotificacoes — r186
// ────────────────────────────────────────────────────────────────────────────
// Regra do módulo: se o usuário tem notificação NÃO LIDA, toda ação executada
// na tela de Tarefas e Pendências (abrir, criar, editar, excluir, salvar,
// mudar filtro...) escancara este pop-up com a lista pendente.
//
// Dentro do pop-up dá pra:
//   - marcar uma notificação como lida (ou todas de uma vez);
//   - clicar na notificação e ser levado direto pro modal da tarefa;
//   - DESABILITAR a aparição por um tempo: +30min / +1h / +4h / +1d.
//     Os botões SOMAM — clicar +1h duas vezes silencia por 2h, e clicar +30min
//     em cima de um silêncio já ativo estende a partir do fim dele.
//
// O silêncio é por usuário e fica no localStorage (não precisa de backend).
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',
  vermelho:'#e53935', verde:   '#10b981',
  roxo:    '#7c3aed', amarelo: '#d97706',
};

// r212: o mapa de emojis por tipo saiu daqui — agora é SVG + rótulo + cor no
//       constants/iconesNotificacoes.jsx, compartilhado com a legenda/filtro.

const CHAVE_SILENCIO = (uid) => `tarefas:notificacoes:silencio:${uid || 'anon'}`;

// Intervalo mínimo entre duas consultas ao backend (evita bater a cada clique)
const INTERVALO_CONSULTA_MS = 15000;

// ─── Helpers ────────────────────────────────────────────────────────────────
function lerSilencio(uid) {
  try {
    const raw = localStorage.getItem(CHAVE_SILENCIO(uid));
    const ts = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(ts) && ts > Date.now() ? ts : 0;
  } catch {
    return 0;
  }
}

function gravarSilencio(uid, ts) {
  try {
    if (ts > Date.now()) localStorage.setItem(CHAVE_SILENCIO(uid), String(ts));
    else localStorage.removeItem(CHAVE_SILENCIO(uid));
  } catch {
    /* localStorage indisponível — silêncio vale só pra sessão em memória */
  }
}

function formatarAte(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const hoje = new Date();
  const mesmoDia = d.toDateString() === hoje.toDateString();
  return mesmoDia ? `${hh}:${mm}` : `${d.toLocaleDateString('pt-BR')} ${hh}:${mm}`;
}

function restanteLegivel(ts) {
  const min = Math.max(0, Math.round((ts - Date.now()) / 60000));
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d${h % 24 ? ` ${h % 24}h` : ''}`;
}

const tempoRelativo = (dataIso) => {
  if (!dataIso) return '';
  const data = new Date(dataIso);
  const diffMin = Math.floor((new Date() - data) / 60000);
  if (diffMin < 1)  return 'agora';
  if (diffMin < 60) return `${diffMin}min`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h}h`;
  const dias = Math.floor(h / 24);
  if (dias < 7) return `${dias}d`;
  return data.toLocaleDateString('pt-BR');
};

// r-now: mesma regra de contraste usada nas tags de cliente das outras telas
//        (Clientes, Acessos): fundo claro → texto escuro.
function corDoTextoNaTag(bg) {
  const hex = (bg || '#003399').replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.5 ? '#1a1a2e' : '#ffffff';
}

// r214: status atual da tarefa referenciada — texto + ícone + cor. O aviso pode
//       ser de dias atrás, então mostrar em que pé a tarefa está agora evita
//       abrir uma a uma só pra descobrir o que já foi resolvido.
function PilulaStatus({ status, statusGeral }) {
  const excluida = statusGeral === 'I';
  const st = excluida
    ? { label: 'Excluída', cor: COR.muted, bg: '#f3f4f6', codigo: '?' }
    : statusDaTarefa(status);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '2px 10px', borderRadius: '20px',
      fontSize: '12px', fontWeight: '700',
      backgroundColor: st.bg, color: st.cor,
      border: `1px solid ${st.cor}55`, whiteSpace: 'nowrap',
    }} title={`Status atual da tarefa: ${st.label}`}>
      {iconeDoStatus(st.codigo, { width: 13, height: 13, color: st.cor })}
      <span>{st.label}</span>
    </span>
  );
}

function TagCliente({ tag, cor }) {
  if (!tag) return null;
  const bg = cor || COR.azul;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 11px',
      borderRadius: '20px', fontSize: '12px', fontWeight: '600',
      backgroundColor: bg, color: corDoTextoNaTag(bg),
      letterSpacing: '0.3px', whiteSpace: 'nowrap',
    }}>{tag}</span>
  );
}

const refCurta = (n) => {
  if (!n?.referencia_id) return '';
  if (n.referencia_tabela === 'tarefas')  return `#${n.referencia_id}`;
  if (n.referencia_tabela === 'negocios') return String(n.referencia_id);
  return '';
};

// ════════════════════════════════════════════════════════════════════════════
// Hook — controla busca, abertura e silêncio
// ────────────────────────────────────────────────────────────────────────────
// Uso:
//   const alerta = useAlertaNotificacoes(usuario);
//   ... alerta.verificar();          // chamar depois de CADA ação da tela
//   <PopupNotificacoesTarefas alerta={alerta} onAbrirTarefa={abrirTarefaPorId} />
// ════════════════════════════════════════════════════════════════════════════
export function useAlertaNotificacoes(usuario) {
  const uid = usuario?.id;
  const [aberto, setAberto]             = useState(false);
  const [notificacoes, setNotificacoes] = useState([]);
  const [silenciadoAte, setSilenciadoAte] = useState(() => lerSilencio(uid));
  const ultimaConsultaRef = useRef(0);
  const consultandoRef    = useRef(false);

  // Usuário resolveu depois da montagem: recarrega o silêncio dele
  useEffect(() => { setSilenciadoAte(lerSilencio(uid)); }, [uid]);

  // Expira o silêncio sozinho (sem depender de nova ação do usuário)
  useEffect(() => {
    if (!silenciadoAte) return undefined;
    const t = setTimeout(() => setSilenciadoAte(0), Math.max(1000, silenciadoAte - Date.now()));
    return () => clearTimeout(t);
  }, [silenciadoAte]);

  // ── Chamado após cada ação da tela ──────────────────────────────────────
  const verificar = useCallback(async () => {
    if (!uid) return;
    if (lerSilencio(uid)) return;                 // desabilitado pelo +30min/+1h/...
    if (consultandoRef.current) return;
    if (Date.now() - ultimaConsultaRef.current < INTERVALO_CONSULTA_MS) return;

    consultandoRef.current = true;
    try {
      const dados = await notificacaoService.listar(uid, { apenasNaoLidas: true });
      ultimaConsultaRef.current = Date.now();
      setNotificacoes(dados || []);
      if ((dados || []).length > 0) setAberto(true);
    } catch (err) {
      // Silencioso — notificação não pode atrapalhar a operação da tela
      console.warn('Falha ao verificar notificações pendentes:', err.message);
    } finally {
      consultandoRef.current = false;
    }
  }, [uid]);

  const fechar = useCallback(() => setAberto(false), []);

  const marcarLida = useCallback(async (id) => {
    setNotificacoes(arr => arr.filter(n => n.id !== id));   // otimista
    try {
      await notificacaoService.marcarLida(id, uid);
    } catch (err) {
      console.warn('Falha ao marcar notificação como lida:', err.message);
    }
  }, [uid]);

  const marcarTodas = useCallback(async () => {
    setNotificacoes([]);
    setAberto(false);
    try {
      await notificacaoService.marcarTodasLidas(uid);
    } catch (err) {
      console.warn('Falha ao marcar todas como lidas:', err.message);
    }
  }, [uid]);

  // Soma o período ao silêncio vigente (ou a agora, se não houver)
  const adiar = useCallback((minutos) => {
    const base = Math.max(Date.now(), lerSilencio(uid) || 0);
    const novo = base + minutos * 60000;
    gravarSilencio(uid, novo);
    setSilenciadoAte(novo);
    // Não fecha: o usuário pode querer somar mais um período antes de sair.
  }, [uid]);

  const reativar = useCallback(() => {
    gravarSilencio(uid, 0);
    setSilenciadoAte(0);
  }, [uid]);

  return {
    aberto, notificacoes, silenciadoAte,
    verificar, fechar, marcarLida, marcarTodas, adiar, reativar,
  };
}

// ─── Botões de adiamento ────────────────────────────────────────────────────
const OPCOES_ADIAR = [
  { label: '+30min', minutos: 30 },
  { label: '+1h',    minutos: 60 },
  { label: '+4h',    minutos: 240 },
  { label: '+1 dia', minutos: 1440 },
];

const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 11000,
    backgroundColor: 'rgba(0,0,0,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Manrope', sans-serif",
  },
  // r-now: pop-up mais largo (a mensagem da tarefa vinha quebrando em 3-4 linhas
  //        num quadro de 520px) e com escala tipográfica maior.
  caixa: {
    width: '880px', maxWidth: 'calc(100vw - 48px)',
    maxHeight: 'calc(100vh - 80px)',
    backgroundColor: COR.branco,
    borderRadius: '12px',
    boxShadow: '0 16px 48px rgba(0,0,0,0.25)',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    padding: '18px 24px',
    borderBottom: `1px solid ${COR.borda}`,
    backgroundColor: COR.cinzaBg,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '12px',
  },
  titulo: {
    fontSize: '15px', fontWeight: '700', color: COR.texto,
    fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  // r212: legenda dos ícones — cada chip também é um filtro por tipo
  legenda: {
    padding: '10px 18px',
    borderBottom: `1px solid ${COR.borda}`,
    backgroundColor: COR.branco,
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
  },
  chip: (ativo, cor) => ({
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    height: '30px', padding: '0 12px',
    border: `1px solid ${ativo ? cor : COR.borda}`,
    borderRadius: '16px',
    backgroundColor: ativo ? cor + '18' : COR.branco,
    color: ativo ? cor : COR.texto,
    fontSize: '12px', fontWeight: '700', cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif", whiteSpace: 'nowrap',
  }),
  // r-now: fundo cinza na lista pra cada aviso (card branco) se destacar
  lista: {
    overflowY: 'auto', flex: 1, minHeight: '100px',
    padding: '14px 18px', backgroundColor: COR.cinzaBg,
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  // r-now: cada notificação virou um card separado, com a cor do tipo na
  //        borda esquerda (antes era só um bloco azul contínuo).
  item: {
    padding: '14px 16px',
    border: `1px solid ${COR.borda}`,
    borderRadius: '8px',
    backgroundColor: COR.branco,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    display: 'flex', gap: '14px', alignItems: 'flex-start',
  },
  rodape: {
    padding: '14px 20px',
    borderTop: `1px solid ${COR.borda}`,
    backgroundColor: COR.cinzaBg,
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  btnAdiar: {
    height: '32px', padding: '0 14px',
    border: `1px solid ${COR.borda}`, borderRadius: '16px',
    backgroundColor: COR.branco, color: COR.texto,
    fontSize: '12px', fontWeight: '700', cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  btnLink: {
    border: 'none', background: 'transparent', color: COR.azul,
    fontSize: '12px', fontWeight: '700', cursor: 'pointer', padding: '4px 6px',
    fontFamily: "'Manrope', sans-serif",
  },
  btnFechar: {
    height: '34px', padding: '0 20px',
    border: 'none', borderRadius: '5px',
    backgroundColor: COR.azul, color: COR.branco,
    fontSize: '13px', fontWeight: '700', cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
};

// ════════════════════════════════════════════════════════════════════════════
// Componente do pop-up
//   alerta:        objeto devolvido por useAlertaNotificacoes
//   onAbrirTarefa: (tarefaId) => void — abre o modal da tarefa na página
// ════════════════════════════════════════════════════════════════════════════
function PopupNotificacoesTarefas({ alerta, onAbrirTarefa }) {
  const { aberto, notificacoes, silenciadoAte, fechar, marcarLida, marcarTodas, adiar, reativar } = alerta;

  // r212: tipos selecionados na legenda. Vazio = mostra todos.
  const [tiposFiltro, setTiposFiltro] = useState([]);

  // Quantos avisos de cada tipo — alimenta o contador dos chips e define
  // quais chips aparecem (só os tipos que existem na lista atual).
  const contagemPorTipo = useMemo(() => {
    const c = {};
    for (const n of notificacoes) {
      const t = tipoNormalizado(n.tipo);
      c[t] = (c[t] || 0) + 1;
    }
    return c;
  }, [notificacoes]);

  const visiveis = useMemo(() => (
    tiposFiltro.length === 0
      ? notificacoes
      : notificacoes.filter(n => tiposFiltro.includes(tipoNormalizado(n.tipo)))
  ), [notificacoes, tiposFiltro]);

  if (!aberto || notificacoes.length === 0) return null;

  const alternarTipo = (codigo) => {
    setTiposFiltro(prev => prev.includes(codigo)
      ? prev.filter(c => c !== codigo)
      : [...prev, codigo]);
  };

  const irParaTarefa = (n) => {
    marcarLida(n.id);
    fechar();
    if (n.referencia_tabela === 'tarefas' && n.referencia_id && onAbrirTarefa) {
      onAbrirTarefa(n.referencia_id);
    }
  };

  return (
    <div style={S.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) fechar(); }}>
      <div style={S.caixa}>
        <div style={S.header}>
          <span style={{ ...S.titulo, display: 'flex', alignItems: 'center', gap: '8px' }}>
            {iconeDaNotificacao('OUTRO', { width: 18, height: 18, color: COR.azul })}
            {tiposFiltro.length > 0
              ? `${visiveis.length} de ${notificacoes.length} notificação(ões) não lida(s)`
              : `${notificacoes.length} notificação(ões) não lida(s)`}
          </span>
          <button style={S.btnLink} onClick={marcarTodas}>Marcar todas como lidas</button>
        </div>

        {/* r212: legenda dos ícones — clicar num tipo filtra a lista */}
        <div style={S.legenda}>
          <span style={{ fontSize: '12px', color: COR.muted, fontWeight: '600', marginRight: '2px' }}>
            Tipos:
          </span>
          {TIPOS_NOTIFICACAO.filter(t => contagemPorTipo[t.codigo] > 0).map(t => {
            const ativo = tiposFiltro.includes(t.codigo);
            return (
              <button
                key={t.codigo}
                style={S.chip(ativo, t.cor)}
                onClick={() => alternarTipo(t.codigo)}
                title={ativo ? `Remover filtro: ${t.label}` : `Filtrar por: ${t.label}`}
              >
                {iconeDaNotificacao(t.codigo, { width: 15, height: 15, color: t.cor })}
                <span>{t.label}</span>
                <span style={{ color: COR.muted, fontWeight: '700' }}>{contagemPorTipo[t.codigo]}</span>
              </button>
            );
          })}
          {tiposFiltro.length > 0 && (
            <button style={S.btnLink} onClick={() => setTiposFiltro([])}>Limpar filtro</button>
          )}
        </div>

        <div style={S.lista}>
          {visiveis.length === 0 && (
            <div style={{
              padding: '28px', textAlign: 'center',
              color: COR.muted, fontSize: '13px',
            }}>
              Nenhum aviso do(s) tipo(s) selecionado(s).
            </div>
          )}
          {visiveis.map(n => {
            const visual = visualDaNotificacao(n.tipo);
            const ehTarefa = n.referencia_tabela === 'tarefas' && !!n.referencia_id;
            // r-now: a linha de contexto (cliente + projeto) só aparece quando
            //        o aviso tem de onde tirar — evita faixa vazia nos avisos
            //        soltos (ex.: tarefa interna sem cliente/negócio).
            // r214: só faz sentido pra aviso de tarefa — e só quando o SELECT
            //       achou a tarefa (aviso órfão vem com status null).
            const temStatus = ehTarefa && !!(n.tarefa_status || n.tarefa_status_geral);
            const temContexto = !!(n.cliente_tag || n.cliente_nome || n.projeto_nome || n.projeto_id);
            return (
              <div key={n.id} style={{ ...S.item, borderLeft: `4px solid ${visual.cor}` }}>
                <div style={{
                  width: '38px', height: '38px', borderRadius: '50%',
                  backgroundColor: visual.cor + '20', color: visual.cor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }} title={visual.label}>
                  {iconeDaNotificacao(visual.codigo, { width: 19, height: 19, color: visual.cor })}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Cliente + Projeto — contexto do aviso, antes do título */}
                  {temContexto && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      flexWrap: 'wrap', marginBottom: '6px',
                    }}>
                      <TagCliente tag={n.cliente_tag} cor={n.cliente_cor_tag} />
                      {!n.cliente_tag && n.cliente_nome && (
                        <span style={{ fontSize: '12px', fontWeight: '700', color: COR.texto }}>
                          {n.cliente_nome}
                        </span>
                      )}
                      {(n.projeto_nome || n.projeto_id) && (
                        <span style={{
                          fontSize: '12px', color: COR.muted, fontWeight: '600',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }} title={`${n.projeto_id || ''} ${n.projeto_nome || ''}`.trim()}>
                          📁 {n.projeto_nome || n.projeto_id}
                        </span>
                      )}
                    </div>
                  )}

                  <div style={{
                    fontSize: '14px', fontWeight: '700', color: COR.texto,
                    marginBottom: '4px', lineHeight: '1.35',
                  }}>
                    {refCurta(n) && (
                      <span style={{
                        display: 'inline-block', marginRight: '6px',
                        padding: '1px 8px', borderRadius: '10px',
                        fontSize: '12px', fontWeight: '700',
                        backgroundColor: visual.cor + '18', color: visual.cor,
                        border: `1px solid ${visual.cor}55`, verticalAlign: '1px',
                      }}>{refCurta(n)}</span>
                    )}
                    {n.titulo}
                  </div>
                  {n.mensagem && (
                    <div style={{ fontSize: '13px', color: COR.muted, lineHeight: '1.45' }}>
                      {n.mensagem}
                    </div>
                  )}
                  <div style={{
                    marginTop: '10px', paddingTop: '8px',
                    borderTop: `1px solid ${COR.borda}`,
                    display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap',
                  }}>
                    <span style={{ fontSize: '12px', color: COR.muted, marginRight: '6px' }}>
                      {tempoRelativo(n.criado_em)}
                    </span>
                    {ehTarefa && (
                      <button style={S.btnLink} onClick={() => irParaTarefa(n)}>
                        Abrir tarefa {refCurta(n)}
                      </button>
                    )}
                    <button
                      style={{ ...S.btnLink, color: COR.muted }}
                      onClick={() => marcarLida(n.id)}
                    >
                      Marcar como lida
                    </button>
                  </div>
                </div>

                {/* r214a: status no canto superior direito do card — o
                    marginLeft:auto empurra pra direita e o alignSelf mantém
                    alinhado ao topo, na mesma linha do título. */}
                {temStatus && (
                  <div style={{ marginLeft: 'auto', alignSelf: 'flex-start', paddingLeft: '10px' }}>
                    <PilulaStatus status={n.tarefa_status} statusGeral={n.tarefa_status_geral} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={S.rodape}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', color: COR.muted, fontWeight: '600' }}>
              Não mostrar por:
            </span>
            {OPCOES_ADIAR.map(o => (
              <button key={o.label} style={S.btnAdiar} onClick={() => adiar(o.minutos)}>
                {o.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
            <span style={{ fontSize: '12px', color: silenciadoAte ? COR.azul : COR.muted }}>
              {silenciadoAte
                ? `Silenciado por ${restanteLegivel(silenciadoAte)} — até ${formatarAte(silenciadoAte)}.`
                : 'Os períodos somam: clique mais de uma vez pra estender.'}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {silenciadoAte > 0 && (
                <button style={S.btnLink} onClick={reativar}>Reativar avisos</button>
              )}
              <button style={S.btnFechar} onClick={fechar}>Fechar</button>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PopupNotificacoesTarefas;
